import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  FormField,
  Select,
  TextInput,
  Text,
  Button,
  Form,
  Anchor,
  Layer,
} from 'grommet';
import axios from '../../../helpers/axios';

import { FieldTeamColor } from '../../../helpers/FormFields';
import { fetchPayload } from './teamInformation.helpers';

import {
  validateHandle,
  validateScoreBoardName,
  validateTeamName,
  validatePostalCode,
  validateNameLocation,
} from './teamInformation.validation';
import {
  TeamInfoFormProps,
  defaultFormValues,
  FormValues,
} from './teamInformation.types';

import {
  generateSeasonOptions,
  getLabelForSeason,
  getTeamAgeLevelOptions,
} from '@helpers/utils';

import { useDeleteOrArchiveTeam } from '@hooks/useTeam';

import TeamArchiveOrDelete from './TeamArchiveOrDelete';
import SHDThemeOverride from '@ui/ThemeOverride/SHDThemeOverride';
import { MoonLoader } from 'react-spinners';

import { analytics } from '@services/analytics';

const TeamInfoForm: React.FC<TeamInfoFormProps> = ({
  selectedTeam,
  onFormUpdate,
}) => {
  const [value, setValue] = useState<FormValues>(defaultFormValues);
  const [currentValues, setCurrentValues] =
    useState<FormValues>(defaultFormValues);
  const [validationError, setValidationError] = useState<string | undefined>(
    undefined
  );
  const [hasChanges, setHasChanges] = useState<boolean>(false);

  const [saving, setSaving] = useState<boolean>(false);

  const [showUserConfirmationPrompt, setShowUserConfirmationPrompt] =
    useState<boolean>(false);

  const [teamRemoved, setTeamRemoved] = useState<boolean>(false);
  const [teamRemovalProcessing, setTeamRemovalProcessing] =
    useState<boolean>(false);

  const {
    archiveTeam,
    deleteTeam,
    canDelete,
    processComplete: teamArchiveOrDeleteCompleted,
    loading: canDeleteOrArchiveTeamLoading,
  } = useDeleteOrArchiveTeam(selectedTeam?._id);

  const [teamArchiveDeleteLabel, setTeamArchiveDeleteLabel] = useState<
    'Archive team' | 'Delete team' | null
  >(null);

  useEffect(() => {
    if (selectedTeam) {
      setValue({
        nameLong: selectedTeam.nameLong,
        nameHandle: selectedTeam.nameHandle,
        nameMed: selectedTeam.nameMed,
        attrib_sportType: selectedTeam.attrib_sportType,
        attrib_season: selectedTeam.attrib_season,
        attrib_ageLevel: selectedTeam.attrib_ageLevel,
        nameLocation: selectedTeam.nameLocation,
        zipCode: selectedTeam.zipCode,
        socialFacebookUrl: selectedTeam.socialFacebookUrl,
        socialInstagramHandle: selectedTeam.socialInstagramHandle,
        socialTwitterHandle: selectedTeam.socialTwitterHandle,
        colorTeamPrimary: selectedTeam.colorTeamPrimary,
      });
      setCurrentValues({
        nameLong: selectedTeam.nameLong,
        nameHandle: selectedTeam.nameHandle,
        nameMed: selectedTeam.nameMed,
        attrib_sportType: selectedTeam.attrib_sportType,
        attrib_season: selectedTeam.attrib_season,
        attrib_ageLevel: selectedTeam.attrib_ageLevel,
        nameLocation: selectedTeam.nameLocation,
        zipCode: selectedTeam.zipCode,
        socialFacebookUrl: selectedTeam.socialFacebookUrl,
        socialInstagramHandle: selectedTeam.socialInstagramHandle,
        socialTwitterHandle: selectedTeam.socialTwitterHandle,
        colorTeamPrimary: selectedTeam.colorTeamPrimary,
      });
    }
    const generatedSeasons = generateSeasonOptions();

    // Check if the previously selected season exists in the generated options
    const selectedSeason = selectedTeam?.attrib_season;
    const seasonExists = generatedSeasons.some(
      (option) => option.value === selectedSeason
    );

    // If the selected season doesn't exist in the options, add it
    if (selectedSeason && !seasonExists) {
      const selectedSeasonLabel = getLabelForSeason(selectedSeason); // A function that maps the value back to its label
      generatedSeasons.push({
        label: selectedSeasonLabel,
        value: selectedSeason,
      });
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [selectedTeam]);

  useEffect(() => {
    if (!canDeleteOrArchiveTeamLoading && canDelete) {
      setTeamArchiveDeleteLabel('Delete team');
    }

    if (!canDeleteOrArchiveTeamLoading && !canDelete) {
      setTeamArchiveDeleteLabel('Archive team');
    }
  }, [canDelete, canDeleteOrArchiveTeamLoading]);

  const onSave = useCallback(async () => {
    setSaving(true);
    const newValue = fetchPayload(value, currentValues);
    try {
      const resp = await axios.put(`/api/team/${selectedTeam?._id}`, {
        teamInfo: newValue,
        isEditPrivacy: false,
      });
      if (resp.status === 200) {
        onFormUpdate(resp.data);
        setSaving(false);
        setHasChanges(false);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      setValidationError(error.response.data.message);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedTeam?._id, currentValues]);

  const onReset = useCallback(() => {
    setValue(currentValues);
    setHasChanges(false);
    setValidationError(undefined);
  }, [currentValues]);

  const onChange = useCallback((nextValue: FormValues) => {
    setHasChanges(true);
    setValue(nextValue);
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, []);

  const handleTeamStatusTracking = useCallback(() => {
    analytics.track('Team Status Update', {
      action_type: canDelete ? 'delete' : 'archive',
      teamId: selectedTeam?._id,
      sport: selectedTeam?.attrib_sportType,
    });
  }, [canDelete, selectedTeam]);

  const handleTeamArchive = useCallback(async () => {
    handleTeamStatusTracking();
    setTeamRemovalProcessing(true);
    await archiveTeam();
  }, [archiveTeam, handleTeamStatusTracking]);

  const handleTeamDelete = useCallback(async () => {
    handleTeamStatusTracking();
    setTeamRemovalProcessing(true);
    await deleteTeam();
  }, [deleteTeam, handleTeamStatusTracking]);

  useEffect(() => {
    if (teamArchiveOrDeleteCompleted) {
      setTeamRemoved(true);
    }
  }, [teamArchiveOrDeleteCompleted]);

  return (
    <Box pad={'medium'}>
      <Form
        value={value}
        onChange={onChange}
        onReset={onReset}
        onSubmit={() => onSave()}
      >
        <Box pad={{ bottom: 'large' }}>
          <Box
            margin="small"
            direction="row"
            flex
            alignContent="center"
            justify="between"
          >
            <Box>
              <Text size="medium" weight="bold">
                Team Color
              </Text>
            </Box>
            <Box>
              {value?.colorTeamPrimary && (
                <FieldTeamColor
                  currentColor={value?.colorTeamPrimary}
                  label={false}
                />
              )}
            </Box>
          </Box>
          <FormField
            name="nameLong"
            htmlFor="name-long-input"
            label={<Text size="small">Team Name</Text>}
            validate={validateTeamName}
          >
            <TextInput
              id="name-long-input"
              name="nameLong"
              placeholder="E.g. San Jose Rabbits"
              size="medium"
            />
          </FormField>
          <FormField
            name="nameHandle"
            htmlFor="name-handle-input"
            label={
              <Box flex direction="row" justify="between">
                <Text size="small">Team Handle</Text>
                <Text size="small">{value?.nameHandle?.length || 0}/20</Text>
              </Box>
            }
            info={
              <Text size="small">
                This is your team profile link, e.g. sidelinehd.com/yourteam
              </Text>
            }
            validate={validateHandle}
          >
            <TextInput
              id="name-handle-input"
              name="nameHandle"
              placeholder="No Spaces"
              size="medium"
              maxLength={20}
              minLength={0}
            />
          </FormField>
          <FormField
            name="nameMed"
            htmlFor="name-med-input"
            label={
              <Box flex direction="row" justify="between">
                <Text size="small">Scoreboard Name</Text>
                <Text size="small">{value?.nameMed?.length || 0}/20</Text>
              </Box>
            }
            info={
              <Text size="small" onChange={() => {}}>
                This is what will display on the scoreboard when streaming.
              </Text>
            }
            validate={validateScoreBoardName}
          >
            <TextInput
              id="name-med-input"
              name="nameMed"
              placeholder="Scoreboard Display"
              size="medium"
              maxLength={20}
              minLength={0}
            />
          </FormField>

          <FormField
            name="attrib_ageLevel"
            htmlFor="attrib-agelevel-input"
            label={<Text size="small">Age level</Text>}
            placeholder="Select Age Level"
          >
            <Select
              name="attrib_ageLevel"
              id="attrib-agelevel-input"
              options={getTeamAgeLevelOptions()}
              valueKey={{ key: 'value', reduce: true }}
              labelKey="label"
              size="medium"
            />
          </FormField>

          <FormField
            name="attrib_season"
            htmlFor="attrib-season-input"
            label={<Text size="small">Season</Text>}
            placeholder="Select Season"
          >
            <Select
              name="attrib_season"
              id="attrib-season-input"
              options={generateSeasonOptions()}
              valueKey={{ key: 'value', reduce: true }}
              labelKey="label"
              size="medium"
            />
          </FormField>

          <FormField
            name="nameLocation"
            htmlFor="name-location-input"
            label={<Text size="small">Team Location</Text>}
            validate={validateNameLocation}
          >
            <TextInput
              id="name-location-input"
              name="nameLocation"
              placeholder="City, State"
              size="medium"
            />
          </FormField>

          <FormField
            name="zipCode"
            htmlFor="zipcode-input"
            label={<Text size="small">Team Zip Code</Text>}
            validate={validatePostalCode}
          >
            <TextInput
              id="zipcode-input"
              name="zipCode"
              placeholder="Zip Code"
              size="medium"
            />
          </FormField>
        </Box>
        <Box
          gap="medium"
          border={{
            side: 'top',
            size: 'xsmall',
            color: '#dddddd',
            style: 'solid',
          }}
          pad={{ top: 'large', bottom: 'large' }}
          alignContent="center"
        >
          <FormField label={<Text size="small">Team Socials</Text>}>
            <Box
              margin="small"
              direction="row"
              flex
              alignContent="center"
              justify="between"
            >
              <Box style={{ marginTop: '.75rem' }}>
                <Text size="large">facebook.com/</Text>
              </Box>
              <Box>
                <TextInput
                  name="socialFacebookUrl"
                  placeholder="FB User"
                  width="small"
                />
              </Box>
            </Box>
            <Box
              direction="row"
              margin="small"
              justify="between"
              flex
              alignContent="center"
            >
              <Box style={{ marginTop: '.75rem' }}>
                <Text size="large">instagram.com/</Text>
              </Box>
              <Box>
                <TextInput
                  name="socialInstagramHandle"
                  placeholder="IG User"
                  width="small"
                />
              </Box>
            </Box>
            <Box
              direction="row"
              margin="small"
              justify="between"
              flex
              alignContent="center"
            >
              <Box style={{ marginTop: '.75rem' }}>
                <Text size="large">x.com/</Text>
              </Box>
              <Box>
                <TextInput
                  name="socialTwitterHandle"
                  placeholder="X User"
                  width="small"
                />
              </Box>
            </Box>
          </FormField>
        </Box>
        <Box direction="row" gap="medium" width="100%" justify="center">
          <Text color="status-error">{validationError}</Text>
        </Box>
        <Box direction="row" gap="medium" width="100%" justify="center">
          <Button
            type="submit"
            primary
            label={saving ? '...Saving' : 'Save'}
            disabled={!hasChanges}
          />
          <Button type="reset" secondary label="Reset" disabled={!hasChanges} />
        </Box>
        <Box
          direction="row"
          gap="medium"
          width="100%"
          justify="center"
          margin={{ top: '21px' }}
        >
          {canDeleteOrArchiveTeamLoading ? (
            <MoonLoader size="18px" />
          ) : (
            <Anchor
              color={'#1c8700'}
              weight={'bold'}
              label={teamArchiveDeleteLabel}
              onClick={() => setShowUserConfirmationPrompt(true)}
              style={{ textDecoration: 'none' }}
            />
          )}
        </Box>
      </Form>

      {showUserConfirmationPrompt && (
        <Layer
          full
          background="#fff"
          onEsc={() => {
            if (!teamRemovalProcessing) {
              setShowUserConfirmationPrompt(false);
            }
          }}
        >
          <SHDThemeOverride>
            <TeamArchiveOrDelete
              team={selectedTeam}
              canDelete={canDelete}
              isTeamRemovalProcessing={teamRemovalProcessing}
              isTeamRemoved={teamRemoved}
              onCancel={() => setShowUserConfirmationPrompt(false)}
              onArchive={handleTeamArchive}
              onDelete={handleTeamDelete}
            />
          </SHDThemeOverride>
        </Layer>
      )}
    </Box>
  );
};

export default TeamInfoForm;
